// We're getting a bunch of html from the server and shoving it into react here.

import * as React from 'react';
import {useDispatch} from 'react-redux';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

import {EmptyStateLarge} from 'shells/empty-states/empty-state-large';

import type {Lead as LeadFragment} from 'nutshell-graphql-types';

import {ClientRouteLegacyLinks} from '../../../client-route-legacy-links';

import {setAgentContext} from '../../team-ai/redux/agent-reducer';
import {LEAD_PAGE} from '../../team-ai/redux/consts';

import {LeadAssignee} from './header/lead-assignee';
import {LeadButtons} from './header/lead-buttons';
import {LeadHeaderLeft} from './header/lead-header-left';
import {LeadReactContainer} from './lead-react-container';

import styles from './lead-contents.module.css';

type Props = {
    history: RouterHistory;
    leadEnclosure: HTMLElement | null | undefined;
    sidebarMarkup: HTMLElement | null | undefined;
    lead: LeadFragment | null | undefined;
    onRenderLeadEnclosure?: () => void;
    isLoadingLead: boolean;
    errorMessage: string | null | undefined;
};

export function LeadContents(props: Props) {
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (props.lead) {
            dispatch(setAgentContext(props.lead.id, LEAD_PAGE, props.lead.name));

            return () => {
                dispatch(setAgentContext(null, null, null));
            };
        }
    }, [props.lead, dispatch]);

    const hasError = props.errorMessage && !props.isLoadingLead;
    const isPermissionsError = props.errorMessage === 'You don’t have permission to view that lead';

    if (hasError) {
        return (
            <div className={styles['error-container']}>
                <EmptyStateLarge
                    icon={
                        isPermissionsError ? (
                            <img src='/include/images/lead/entity-permissions.svg' />
                        ) : (
                            <img src='/include/images/lead/lead-not-found.svg' />
                        )
                    }
                    title='Oh no! Something went wrong.'
                    subtitle={props.errorMessage}
                />
            </div>
        );
    }

    return (
        <div
            className={
                props.sidebarMarkup && props.leadEnclosure && props.lead
                    ? styles['lead-contents']
                    : styles['lead-contents--loading']
            }
        >
            <div className={styles['lead-column-left']}>
                {props.lead && !props.isLoadingLead && (
                    <>
                        <LeadHeaderLeft leadId={props.lead.id} />
                        <LeadReactContainer id={props.lead.id} />
                        {props.sidebarMarkup && props.leadEnclosure ? (
                            <div
                                id='lead-enclosure'
                                ref={(ref) => {
                                    if (ref && props.onRenderLeadEnclosure) {
                                        props.onRenderLeadEnclosure();
                                    }
                                }}
                                data-entity-id={props.leadEnclosure.dataset.entityId}
                                data-api-id={props.leadEnclosure.dataset.apiId}
                                dangerouslySetInnerHTML={{
                                    __html: props.leadEnclosure.innerHTML,
                                }}
                            />
                        ) : undefined}
                    </>
                )}
            </div>
            <div className={styles['lead-column-right']}>
                {props.lead && !props.isLoadingLead && (
                    <>
                        <LeadButtons leadId={props.lead.id} />
                        <LeadAssignee leadId={props.lead.id} />
                        {props.sidebarMarkup && props.leadEnclosure ? (
                            <ClientRouteLegacyLinks history={props.history}>
                                {({interceptClicks}) => (
                                    <div
                                        onClick={interceptClicks}
                                        dangerouslySetInnerHTML={{
                                            // @ts-expect-error
                                            __html:
                                                props.sidebarMarkup &&
                                                props.sidebarMarkup.innerHTML,
                                        }}
                                    />
                                )}
                            </ClientRouteLegacyLinks>
                        ) : undefined}
                    </>
                )}
            </div>
        </div>
    );
}
